<template>
  <div
    class="health-check-widget"
    :class="[connectionStatus, { 'minimal': minimal }]"
    :title="connectionTitle"
  >
    <div class="status-content">
      <span class="indicator-dot"></span>
      <span v-if="!minimal" class="indicator-text">{{ connectionText }}</span>
    </div>
    <button
      v-if="!minimal"
      @click="handleRefresh"
      :disabled="isChecking"
      class="refresh-btn"
    >
      ↻
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useHealthCheck } from '@/composables/useHealthCheck'

const props = defineProps({
  minimal: {
    type: Boolean,
    default: false
  }
})

const { isConnected, connectionStatus, checkHealth } = useHealthCheck()

const isChecking = computed(() => {
  return connectionStatus.value === 'checking'
})

const connectionText = computed(() => {
  switch (connectionStatus.value) {
    case 'checking':
      return 'Verificando'
    case 'connected':
      return 'Connesso'
    case 'disconnected':
      return 'Disconnesso'
    default:
      return 'Sconosciuto'
  }
})

const connectionTitle = computed(() => {
  switch (connectionStatus.value) {
    case 'checking':
      return 'Verificando connessione API...'
    case 'connected':
      return 'API Online - Tutti i servizi attivi'
    case 'disconnected':
      return 'API Offline - Verificare la connessione'
    default:
      return 'Stato connessione sconosciuto'
  }
})

const handleRefresh = async () => {
  await checkHealth()
}
</script>

<style scoped>
.health-check-widget {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  border: 1px solid currentColor;
  min-width: 100%; /* Occupa tutta la larghezza del contenitore verticale */
  opacity: 0.7;
  height: 22px; /* Altezza coordinata */
  box-sizing: border-box;
}

.health-check-widget.minimal {
  border: none;
  min-width: auto;
  padding: 0;
  height: auto;
  background: transparent !important;
  opacity: 1;
}

.health-check-widget.minimal .indicator-dot {
  width: 7px;
  height: 7px;
  margin: 0;
}

.health-check-widget:hover {
  opacity: 1;
}

.status-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.indicator-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-shadow: 0 0 4px currentColor;
  animation: pulse 2s infinite;
  flex-shrink: 0;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.health-check-widget.checking {
  background-color: rgba(255, 243, 205, 0.1);
  color: #ffc107;
  border-color: rgba(255, 193, 7, 0.3);
}

.health-check-widget.checking .indicator-dot {
  background-color: #ffc107;
}

.health-check-widget.connected {
  background-color: rgba(212, 237, 218, 0.1);
  color: #28a745;
  border-color: rgba(40, 167, 69, 0.3);
}

.health-check-widget.connected .indicator-dot {
  background-color: #28a745;
}

.health-check-widget.disconnected {
  background-color: rgba(248, 215, 218, 0.1);
  color: #dc3545;
  border-color: rgba(220, 53, 69, 0.3);
}

.health-check-widget.disconnected .indicator-dot {
  background-color: #dc3545;
}

.refresh-btn {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid currentColor;
  color: inherit;
  cursor: pointer;
  font-size: 12px;
  padding: 2px 4px;
  border-radius: 4px;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  min-height: 20px;
  line-height: 1;
  font-weight: bold;
  flex-shrink: 0;
}

.refresh-btn:not(:disabled):hover {
  background-color: rgba(0, 0, 0, 0.15);
  transform: rotate(-25deg) scale(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.refresh-btn:not(:disabled):active {
  transform: rotate(-25deg) scale(0.95);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 1024px) {
  .health-check-widget {
    padding: 6px 10px;
    font-size: 11px;
    gap: 6px;
  }

  .indicator-dot {
    width: 8px;
    height: 8px;
  }

  .refresh-btn {
    font-size: 14px;
    min-width: 24px;
    min-height: 24px;
    padding: 3px 5px;
  }
}

@media (max-width: 768px) {
  .health-check-widget {
    padding: 6px 8px;
    font-size: 10px;
    gap: 4px;
  }

  .indicator-text {
    display: none;
  }

  .indicator-dot {
    width: 8px;
    height: 8px;
  }

  .refresh-btn {
    font-size: 13px;
    min-width: 22px;
    min-height: 22px;
    padding: 2px 4px;
  }
}
</style>
